/**
 * Canonical customer-facing line presentation — shared by interactive proposal and true-PDF.
 * Never promotes description into the title merely because catalogue match / model is missing.
 */
import { resolveStagingLineVisibleDescription } from "./dtoolsCatalogueMatch";
import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import { isServiceLine } from "./dtoolsReviewNavigation";
import {
  formatProductBrandModelReference,
  resolveProductDisplayName,
} from "./productDisplayName";

function clean(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

function normKey(value: string): string {
  return clean(value).toLowerCase().replace(/\s+/g, " ");
}

/** True when text looks like a multi-sentence body description, not a product/service title. */
export function looksLikeBodyDescription(text: string): boolean {
  const t = clean(text);
  if (!t) return false;
  if (t.length > 90) return true;
  if (/[.!?].+\S/.test(t) && t.length > 48) return true;
  if (/^(the|clarity)\s+.+\s+is\s+(a|an)\s+/i.test(t)) return true;
  return false;
}

/**
 * Short title candidate from polished/dtools — first clause only, never the full body.
 * Returns empty when the only available text is a long description.
 */
function shortTitleFromDescriptionFields(line: DtoolsStagingLine): string {
  for (const raw of [line.polishedDescription, line.dtoolsDescription]) {
    const text = clean(raw);
    if (!text) continue;
    if (looksLikeBodyDescription(text)) {
      const first = text.split(/[.!?]/)[0]?.trim() ?? "";
      if (first && first.length <= 80 && !looksLikeBodyDescription(first)) return first;
      continue;
    }
    if (text.length <= 80) return text;
  }
  return "";
}

export type ProposalLinePresentation = {
  lineId: string;
  customerTitle: string;
  customerDescription: string;
  brandLabel: string;
  /** Technical model code for equipment; empty when the schedule title column shows customerTitle instead. */
  modelLabel: string;
  /** Value drawn in the schedule Model / Product Title column. */
  scheduleTitle: string;
  roomLabel: string;
  quantity: number;
  qtyLabel: string;
  isService: boolean;
  isSupportingComponent: boolean;
  supportingComponentLabel: string;
  parentId: string | null;
  unitPrice: number;
  lineTotal: number;
  displayPrice: number;
};

function formatQtyLabel(qty: number): string {
  if (!Number.isFinite(qty)) return "0";
  if (Number.isInteger(qty)) return String(qty);
  return String(qty);
}

/**
 * Resolve the same customer-facing title the interactive proposal uses.
 * Display Name wins; never use the body description as the title.
 */
export function resolveProposalLineCustomerTitle(line: DtoolsStagingLine): string {
  const fromResolver = resolveProductDisplayName({
    brand: line.manufacturer,
    manufacturer: line.manufacturer,
    model: line.model,
    displayName: line.displayName,
    description: null, // never feed description into title resolver
  });
  if (fromResolver && fromResolver !== "Product" && !looksLikeBodyDescription(fromResolver)) {
    return fromResolver;
  }

  const stored = clean(line.displayName);
  if (stored && !looksLikeBodyDescription(stored)) return stored;

  const brandModel = formatProductBrandModelReference({
    brand: line.manufacturer,
    manufacturer: line.manufacturer,
    model: line.model,
  });
  if (brandModel) return brandModel;

  const short = shortTitleFromDescriptionFields(line);
  if (short) return short;

  if (isServiceLine(line)) return "Service";
  return fromResolver || "Product";
}

/** Same visible description chain as Link A / Link B — never truncated or rewritten. */
export function resolveProposalLineCustomerDescription(line: DtoolsStagingLine): string {
  return clean(resolveStagingLineVisibleDescription(line));
}

/**
 * Compact supporting-component name only (legacy Clarity quotation behaviour).
 * Prefer display name when it is a short name; else model; never body copy.
 */
export function resolveProposalSupportingComponentLabel(line: DtoolsStagingLine): string {
  const model = clean(line.model);
  const displayName = clean(line.displayName);
  const brand = clean(line.manufacturer);

  if (displayName && !looksLikeBodyDescription(displayName)) {
    if (!model || displayName.toLowerCase() !== model.toLowerCase()) return displayName;
  }
  if (model) return model;
  if (displayName && !looksLikeBodyDescription(displayName)) return displayName;

  const title = resolveProposalLineCustomerTitle(line);
  if (title && !looksLikeBodyDescription(title)) return title;
  if (brand) return brand;
  return "Component";
}

/**
 * Full normalized presentation for one staging line.
 * Interactive proposal and true-PDF must both consume this (or the same underlying resolvers).
 */
export function resolveProposalLinePresentation(
  line: DtoolsStagingLine,
  options?: { roomLabel?: string },
): ProposalLinePresentation {
  const isService = isServiceLine(line);
  const customerTitle = resolveProposalLineCustomerTitle(line);
  let customerDescription = resolveProposalLineCustomerDescription(line);

  // Avoid duplicating the title into the description column when they are identical.
  if (customerDescription && normKey(customerDescription) === normKey(customerTitle)) {
    customerDescription = "";
  }

  const brandLabel = isService
    ? clean(line.manufacturer) || "Clarity"
    : clean(line.manufacturer);
  const modelLabel = isService ? "" : clean(line.model);
  // Schedule title column: services / blank-model lines use customer title; equipment uses model code.
  const scheduleTitle = isService || !modelLabel ? customerTitle : modelLabel;

  const qty = Number(line.quantity);
  const quantity = Number.isFinite(qty) ? qty : 0;

  return {
    lineId: line.id,
    customerTitle,
    customerDescription,
    brandLabel,
    modelLabel,
    scheduleTitle,
    roomLabel: clean(options?.roomLabel) || clean(line.roomName) || (isService ? "Services" : "Room"),
    quantity,
    qtyLabel: formatQtyLabel(quantity),
    isService,
    isSupportingComponent: line.isAccessory === true || Boolean(clean(line.parentId)),
    supportingComponentLabel: resolveProposalSupportingComponentLabel(line),
    parentId: clean(line.parentId) || null,
    unitPrice: Number(line.unitPrice) || 0,
    lineTotal: Number(line.lineTotal) || 0,
    displayPrice: Number(line.lineTotal) || 0,
  };
}

/** Parity helper: interactive card title + description for the same line. */
export function resolveInteractiveProposalLineCopy(line: DtoolsStagingLine): {
  title: string;
  description: string;
} {
  const presentation = resolveProposalLinePresentation(line);
  return {
    title: presentation.customerTitle,
    description: resolveProposalLineCustomerDescription(line),
  };
}
